import React, { useEffect, useState } from "react";

import LoggedOutPage from "../components/LoggedOutPage";
import { useSearchParams } from "react-router-dom";
import Loading from "../components/Loading";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";
import { Alert, AlertProps, Stack } from "@mui/material";
import CRUDForm from "./CRUDForm";

const alertTranslations = defineMessages({
    unauthorized_to_access_page: {
        id: "error_page.unauthorized_to_access_page",
        defaultMessage: "You are not authorized to access this page"
    },
    unexpected_error: {
        id: "login.unexpected_error",
        defaultMessage: "Unexpected error when contacting the server. Please, try again!"
    },
    registration_successful: {
        id: "registration_successful",
        defaultMessage: "Registration successful"
    }
})

const Registration: React.FC = () => {
    // a single alert when we are done (with success or permanent failure)
    const [alert, setAlert] = useState<{ severity: AlertProps["severity"]; label: MessageDescriptor } | null>(null);
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [tokenValid, setTokenValid] = useState(false);

    useEffect(() => {
        setTokenValid(false);
        async function checkToken() {
            try {
                const response = await fetch(`/public/check_token/${token}`);
                if (response.ok) {
                    setTokenValid(true);
                } else {
                    setAlert({ severity: "error", label: alertTranslations.unauthorized_to_access_page });
                }
            } catch {
                setAlert({ severity: "error", label: alertTranslations.unexpected_error });
            }
        }

        checkToken();
    }, [token]);

    if (alert) {
        return (
            <LoggedOutPage title={<FormattedMessage id="register" defaultMessage="Registration" />}>
                <Stack spacing={2} alignItems="center">
                    <Alert severity={alert.severity}>
                        <FormattedMessage {...alert.label} />
                    </Alert>
                    {alert.severity === "success" && (
                        <a href="/public/login">
                            <FormattedMessage id="go_to_login" defaultMessage="Go to login page" />
                        </a>
                    )}
                </Stack>
            </LoggedOutPage>
        );
    }

    // display loading while checking token
    if (!tokenValid) {
        return (
            <LoggedOutPage title={<FormattedMessage id="register" defaultMessage="Registration" />}>
                <Loading />
            </LoggedOutPage>
        );
    }

    return (
        <LoggedOutPage title={<FormattedMessage id="register" defaultMessage="Registration" />}>
            <CRUDForm
                resourceName="user"
                action="create"
                formFields={[
                    "password",
                    "retype_password",
                    "given_name",
                    "family_name",
                    "gender",
                    "birthdate",
                    "locale",
                ]}
                onSuccess={() => {
                    setAlert({ severity: "success", label: alertTranslations.registration_successful });
                }}
                extraRequestData={{
                    token,
                }}
                overrideSubmitURI="/public/register"
            />
        </LoggedOutPage>
    );
};

export default Registration;
